import React, { useEffect, useState } from "react";
import { IconActionKey, IconArrowDown } from "assets/svg";
import {
  Input,
  message,
  Checkbox,
  Button,
  Radio,
  PaginationProps,
  CheckboxProps,
} from "antd";
import {
  DISPOSAL_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SORT,
} from "common/constants";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { fetchProducts } from "reducers/productSlice";
import { Product, ProductFilter } from "common/interfaces";
import { ProductItem } from "components/ProductItem";
import { PaginateList } from "components/PaginateList";
import { useCommonContext } from "contexts/CommonContext";

const ProductList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { productData, error } = useSelector(
    (state: RootState) => state.product,
  );
  const controller = new AbortController();
  const defaultValues: ProductFilter = {
    search: "",
    category_id: [
      PRODUCT_CATEGORY.PLASTIC.value,
      PRODUCT_CATEGORY.METAL.value,
      PRODUCT_CATEGORY.WOOD.value,
      PRODUCT_CATEGORY.GLASS_CERAMIC.value,
      PRODUCT_CATEGORY.RUBBER.value,
      PRODUCT_CATEGORY.PAPER.value,
      PRODUCT_CATEGORY.FIBER.value,
      PRODUCT_CATEGORY.MIXTURE_OTHER.value,
    ],
    state: [
      PRODUCT_STATE.GOOD.value,
      PRODUCT_STATE.USE.value,
      PRODUCT_STATE.DAMAGE.value,
      PRODUCT_STATE.UNKNOWN.value,
    ],
    disposal_type: [
      DISPOSAL_TYPE.RECYCLING.value,
      DISPOSAL_TYPE.FINAL_DISPOSAL.value,
      DISPOSAL_TYPE.UNKNOWN.value,
    ],
    sort: SORT.NEWEST.value,
  };
  const { handleSubmit, control, watch, reset } = useForm<ProductFilter>({
    defaultValues,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const { setHeaderTitle } = useCommonContext();

  const productName: string = watch("search");
  const categoryId: string[] = watch("category_id");
  const state: string[] = watch("state");
  const disposalType: string[] = watch("disposal_type");
  const sort: string = watch("sort");

  const productList: ProductFilter = {
    search: productName,
    category_id: categoryId,
    state: state,
    disposal_type: disposalType,
    sort: sort,
  };

  const plainCategoryOptions = Object.values(PRODUCT_CATEGORY).map(
    (o) => o.value,
  );
  const plainTypeOptions = Object.values(DISPOSAL_TYPE).map((o) => o.value);
  const plainStateOptions = Object.values(PRODUCT_STATE).map((o) => o.value);

  const [checkedCategoryList, setCheckedCategoryList] =
    useState<string[]>(plainCategoryOptions);
  const [checkedTypeList, setCheckedTypeList] =
    useState<string[]>(plainTypeOptions);
  const [checkedStateList, setCheckedStateList] =
    useState<string[]>(plainStateOptions);

  const checkAllCategory =
    plainCategoryOptions.length === checkedCategoryList.length;
  const indeterminateCategory =
    checkedCategoryList.length > 0 &&
    checkedCategoryList.length < plainCategoryOptions.length;

  const checkAllType = plainTypeOptions.length === checkedTypeList.length;
  const indeterminateType =
    checkedTypeList.length > 0 &&
    checkedTypeList.length < plainTypeOptions.length;

  const checkAllState = plainStateOptions.length === checkedStateList.length;
  const indeterminateState =
    checkedStateList.length > 0 &&
    checkedStateList.length < plainStateOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const onChangePage: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
    dispatch(fetchProducts(productList, page, controller.signal));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onReset = () => {
    reset();
    setCurrentPage(0);
    setIsSearch(false);
    setCheckedCategoryList(plainCategoryOptions);
    setCheckedTypeList(plainTypeOptions);
    setCheckedStateList(plainStateOptions);
    dispatch(fetchProducts(defaultValues, 0, controller.signal));
  };

  const onSubmit = () => {
    setCurrentPage(0);
    dispatch(fetchProducts(productList, 0, controller.signal));
  };

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    const currentValues: ProductFilter = {
      search: productName,
      category_id: categoryId,
      state,
      disposal_type: disposalType,
      sort,
    };
    const isDefault =
      JSON.stringify(currentValues) === JSON.stringify(defaultValues);
    setIsSearch(!isDefault);
  }, [productName, categoryId, state, disposalType, sort]);

  useEffect(() => {
    dispatch(fetchProducts(productList, 0, controller.signal));
    setHeaderTitle("商品を検索する");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full product-list">
      <div className="self-stretch w-full min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-10 overflow-hidden">
        <div className="self-stretch flex flex-col justify-start items-start gap-[10px] md:gap-2.5">
          <Controller
            control={control}
            name="search"
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="なにをお探しですか？"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          {!showFilter && (
            <div
              data-プロパティ1="base"
              className="inline-flex justify-start items-center cursor-pointer"
              onClick={toggleFilter}
            >
              <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                検索条件を追加
              </div>
              <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
            </div>
          )}
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    カテゴリー
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start product-category">
                    <Controller
                      control={control}
                      name="category_id"
                      defaultValue={plainCategoryOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedCategoryList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainCategoryOptions
                            : [];
                          setCheckedCategoryList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateCategory}
                              onChange={onCheckAllChange}
                              checked={checkAllCategory}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(PRODUCT_CATEGORY)}
                              value={checkedCategoryList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    状態
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="state"
                      defaultValue={plainStateOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedStateList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainStateOptions
                            : [];
                          setCheckedStateList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateState}
                              onChange={onCheckAllChange}
                              checked={checkAllState}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(PRODUCT_STATE)}
                              value={checkedStateList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    処理区分
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start product-category">
                    <Controller
                      control={control}
                      name="disposal_type"
                      defaultValue={plainTypeOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedTypeList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainTypeOptions
                            : [];
                          setCheckedTypeList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateType}
                              onChange={onCheckAllChange}
                              checked={checkAllType}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(DISPOSAL_TYPE)}
                              value={checkedTypeList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group {...field} options={Object.values(SORT)} />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                    {isSearch ? "検索結果一覧" : "おすすめ"}
                  </div>
                </div>
              </div>
            </div>
            <PaginateList<Product>
              items={productData?.data?.items ?? []}
              renderItem={(item) => <ProductItem item={item} />}
              currentPage={currentPage}
              total={productData?.data?.meta.total_items ?? 0}
              pageSize={10}
              onChangePage={onChangePage}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProductList;
